import { isEmpty, truncate } from 'lodash';
import { useRouter } from 'next/router';
import * as React from 'react';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';
import { SearchOutSideClick } from './SearchOutSideClick';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import {
  ResultPreviewListByType,
} from './AutocomplePreview/ResultPreviewListByType';
import {
  InputLightStylePurpleSearchBar,
} from '@doltech/ui/lib/figma/Input/InputLightStylePurpleSearchBar/InputLightStylePurpleSearchBar';
import { landingRoutingUrls } from '@doltech/core/lib/routing/grammar/grammar-routing.config';
import { useGetRecommendsSearch, useGetResultsFilterByType } from '../../../shared/apis/landing-grammar-search';
import {
  SuggestPreviewListByType,
} from './AutocomplePreview/SuggestPreviewListByType';
import { getFinalUrlGrammar } from '../../../shared/utils';


const SEARCH_QUERY_MAX_LENGTH = 100;
const PAGE_SIZE_EACH_TYPE_OF_RESULTS = 3;

const DivWrapper = styled(Grid).withConfig({
  componentId: 'SearchEngine_ElasticSearchWithAutocomplete_DivWrapper',
} as any)`
  background: ${colorsV2.white100};
  border: 1px solid ${colorsV2.gray5};
  box-sizing: border-box;
  box-shadow: 0 24px 32px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.04),
  0 4px 8px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  text-align: left;

  width: 100%;
  top: 52px;
  z-index: 1;
  padding: 16px 0;
  position: fixed;
  right: 0;

  > span {
    margin-left: 12px;
  }

  ${fromScreen(769)} {
    position: absolute;
  }

  ${fromScreen(1128)} {
    width: 340px;
  }
`;

const SearchContainer = styled.div.withConfig({
  componentId: 'SearchEngine_ElasticSearchWithAutocomplete_SearchContainer',
} as any)`
  display: flex;
  justify-content: center;
  position: relative;
`;

const Wrapper = styled.div.withConfig({
  componentId: 'SearchEngine_ElasticSearchWithAutocomplete_Wrapper',
} as any)`
  position: relative;
  width: 100%;
`;

const OverLay = styled.div`
  width: 100%;
  height: 100vh;
  top: 65px;
  left: 0;
  background: ${colorsV2.black100};
  opacity: 0.5;
  position: fixed;
`;

interface ElasticSearchWithAutocompleteProps {
  defaultValue?: string;
  onEnter: (value: any) => void;
}

export const ElasticSearchWithAutocompleteGrammar = (props: ElasticSearchWithAutocompleteProps) => {
  const { defaultValue = null } = props;
  const router = useRouter();
  const [value, setValue] = React.useState(
    truncate(defaultValue, { length: SEARCH_QUERY_MAX_LENGTH, omission: '' }),
  );
  const [showRecommendData, setShowRecommendData] = React.useState(Boolean(value));
  const [showResultData, setShowResultData] = React.useState(false);
  const [inputFocus, setInputFocus] = React.useState(false);

  const { searchTheories, searchExercises } = useGetResultsFilterByType({
    value,
    pageCurrent: 1,
    size: PAGE_SIZE_EACH_TYPE_OF_RESULTS,
    enabled: inputFocus,
  });
  const { data: searchRecommends } = useGetRecommendsSearch({
    enabled: inputFocus
  });
  const notShowResultBox = (isEmpty(searchTheories) && isEmpty(searchExercises)) || !showResultData;

  const showRecommendBox = showRecommendData && !isEmpty(searchRecommends);

  const handleOnItemClick = React.useCallback(() => {
    setShowResultData(false);
    setShowRecommendData(false);
  }, []);



  const handlePressEnter = React.useCallback((searchValue) => {
    setShowResultData(false);
    setShowRecommendData(false);
    const foundExtractMatch = (searchTheories || searchExercises)?.find((item) => item?.title?.raw === searchValue);

    if (foundExtractMatch && foundExtractMatch?.url?.raw?.length > 0) {
      router.push(getFinalUrlGrammar(foundExtractMatch?.url?.raw));
      return;
    }

    if (router.asPath === landingRoutingUrls.GRAMMAR_SEARCH_PATH) {
      router.replace({
        pathname: landingRoutingUrls.GRAMMAR_SEARCH_PATH,
        query: {
          q: searchValue,
        },
      });
      return;
    }
    router.push({
      pathname: landingRoutingUrls.GRAMMAR_SEARCH_PATH,
      query: {
        q: searchValue,
      },
    });
  }, [router, value, searchExercises, searchTheories]);

  const handleOnSearch = React.useCallback((searchValue) => {
    setValue(searchValue);
    setShowResultData(searchValue?.length > 0);
    setShowRecommendData(searchValue?.length === 0);
  }, []);

  const handleOnFocus = React.useCallback((searchValue) => {
    setInputFocus(true);
    setShowRecommendData(true);
    if (!showResultData) {
      setShowResultData(searchValue?.length > 0);
    }
    if (!showRecommendData) {
      setShowRecommendData(searchValue?.length === 0);
    }
  }, [showRecommendData, showResultData]);


  return (
    <SearchContainer>
      <Wrapper>
        <InputLightStylePurpleSearchBar
          className="search-bar"
          placeholder="Search"
          searchOnEnter={handlePressEnter}
          searchOnChange={handleOnSearch}
          searchOnBlur={() => setInputFocus(false)}
          searchOnFocus={handleOnFocus}
          onSearchClear={() => handleOnSearch(null)}
        />
        {showRecommendBox && (
          <SearchOutSideClick
            outSideClick={() => {
              if (!inputFocus) {
                setShowRecommendData(false);
              }
            }}
          >
            <DivWrapper columns="minmax(0, 1fr)" rows="repeat(auto-fill)" gap="0">
              <SuggestPreviewListByType
                data={searchRecommends}
                onItemClick={handleOnItemClick}
              />
            </DivWrapper>
          </SearchOutSideClick>
        )}
        {notShowResultBox ? null : (
          <SearchOutSideClick
            outSideClick={() => {
              if (!inputFocus) {
                setShowResultData(false);
              }
            }}
          >
            <DivWrapper columns="minmax(0, 1fr)" rows="repeat(auto-fill)" gap="0">
              <Grid columns="minmax(0, 1fr)" rows="repeat(auto-fill)" gap="0">
                <ResultPreviewListByType
                  data={searchTheories}
                  label="LÝ THUYẾT"
                  onItemClick={handleOnItemClick}
                />
                <ResultPreviewListByType
                  data={searchExercises}
                  label="BÀI TẬP"
                  onItemClick={handleOnItemClick}
                />
              </Grid>
            </DivWrapper>
          </SearchOutSideClick>
        )}
      </Wrapper>
      {(notShowResultBox || !showRecommendBox) ? null : <OverLay/>}
    </SearchContainer>
  );
};
